import { useState } from 'react'
import { useFormik } from 'formik'
import * as yup from 'yup'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useQueryClient } from '@tanstack/react-query'

import { Box, Divider, Modal, Text, Typography } from '@percent/lemonade'
import {
  ErrorView,
  Loader,
  PercentButton,
  FieldInput,
  UpdateModalInformation
} from '@percent/admin-dashboard/common/components'
import { ReactComponent as CloseIcon } from '@percent/admin-dashboard/common/assets/images/close.svg'
import styles from './UpdateWebsiteModal.module.scss'
import { UpdateWebsiteModalProps } from './UpdateWebsiteModal.types'
import { useServices } from '@percent/admin-dashboard/containers/service/ServiceContext'
import { PatchOrganisationParams } from '@percent/admin-dashboard/api/types'
import { useMutation } from '@percent/utility'

export function UpdateWebsiteModal({
  organisation,
  refresh,
  agentVerificationId,
  isModalOpened,
  setModalOpened
}: UpdateWebsiteModalProps) {
  const { t } = useTranslation()
  const [isSent, setIsSent] = useState(false)
  const { adminService } = useServices()
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  const { values, setFieldValue, handleBlur, touched, errors, dirty, isValid } = useFormik({
    initialValues: {
      website: organisation?.website
    },
    validationSchema: () =>
      yup.object().shape({
        website: yup
          .string()
          .url(t('errorMessage.validWebsite'))
          .max(255, t('errorMessage.shouldNotExceed255'))
          .required('Required')
      }),
    onSubmit: () => {}
  })

  const [{ isPending, errorMessage }, { apiFunc: aproveFunc }] = useMutation(adminService.editOrganisation, () => {
    setIsSent(true)
    queryClient.invalidateQueries({ queryKey: ['/organisations', organisation?.id] })
  })

  const handleUpdate = () => {
    aproveFunc({ id: organisation?.id, payload: { website: values.website } } as PatchOrganisationParams)
  }

  const handleCloseModalAfterUpdate = () => {
    refresh()
    setIsSent(false)
    navigate(`/agent-verification/${agentVerificationId}`)
    setModalOpened(false)
  }

  return (
    <Modal open={isModalOpened} onClose={() => setModalOpened(false)}>
      {isSent ? (
        <UpdateModalInformation
          handleClick={handleCloseModalAfterUpdate}
          setModalOpened={setModalOpened}
          title={t('dialog.updateWebsite.websiteUpdated')}
          subtitle={<Typography variant="large">{t('dialog.updateWebsite.websiteStored')}</Typography>}
          buttonTitle={t('button.goBackToDetails')}
        />
      ) : (
        <Box className={styles.wrapper}>
          <Box className={styles.titleWrapper}>
            <Text className={styles.title}>
              {t(`dialog.updateWebsite.${organisation?.website ? 'editWebsite' : 'addWebsite'}`)}
            </Text>
            <CloseIcon onClick={() => setModalOpened(false)} />
          </Box>
          <Divider className={styles.divider} />
          <Box className={styles.textWrapper}>
            <Typography variant="large" className={styles.bodyText}>
              {t('dialog.updateWebsite.body')}
            </Typography>
          </Box>
          <FieldInput
            name="website"
            label={t('form.nonprofitWebsite')}
            placeHolder={t('form.website')}
            onChange={(value: string) => setFieldValue('website', value)}
            value={values.website}
            handleBlur={handleBlur}
            error={errors.website}
            touched={touched.website}
          />
          {errorMessage && <ErrorView errorMessage={errorMessage} />}
          {isPending ? (
            <Loader />
          ) : (
            <Box className={styles.buttonWrapper}>
              <PercentButton title={t('button.submit')} onClick={handleUpdate} disabled={!(isValid && dirty)} />
              <PercentButton
                title={t('button.cancel')}
                onClick={() => setModalOpened(false)}
                textColour="secondaryTextColour"
                outlined
              />
            </Box>
          )}
        </Box>
      )}
    </Modal>
  )
}
